import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Heart, Sparkles, Users, Shield, Play, ChevronRight } from 'lucide-react';

const features = [
  { icon: <Heart className="w-6 h-6" />, titleKey: 'liveVideoRooms', descKey: 'liveVideoDesc' },
  { icon: <Sparkles className="w-6 h-6" />, titleKey: 'interactiveGames', descKey: 'gamesDesc' },
  { icon: <Users className="w-6 h-6" />, titleKey: 'communityEvents', descKey: 'eventsDesc' },
  { icon: <Shield className="w-6 h-6" />, titleKey: 'safeEnvironment', descKey: 'safeDesc' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { lang, toggleLanguage, t } = useLanguage();

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#050108] overflow-hidden">
      <button data-testid="language-toggle" onClick={toggleLanguage}
        className="fixed top-6 right-6 z-50 px-4 py-2 rounded-full glass text-sm font-medium hover:bg-white/10 transition-all">
        {lang === 'en' ? '\u{1F1EE}\u{1F1F9} IT' : '\u{1F1EC}\u{1F1E7} EN'}
      </button>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center px-6">
        <div className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=1920&q=80')` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050108]/80 via-[#050108]/60 to-[#050108]" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FF007F]/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#E0115F]/20 rounded-full blur-[120px]" />

        <div className="relative z-10 text-center max-w-4xl mx-auto animate-fade-in">
          <div className="mb-8">
            <h2 className="text-lg tracking-[0.3em] text-[#FF007F] uppercase font-medium mb-2">FlirtyLive</h2>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            {t('heroTitle')} <span className="gradient-text neon-text">{t('heroHighlight')}</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 mb-12 max-w-2xl mx-auto leading-relaxed">
            {t('heroSub')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <button data-testid="enter-live-btn" onClick={() => navigate('/auth')}
              className="group gradient-button rounded-full px-10 py-4 text-lg font-semibold flex items-center gap-3">
              <Play className="w-5 h-5" />
              {t('enterLive')}
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button data-testid="learn-more-btn"
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              className="rounded-full px-10 py-4 text-lg font-semibold bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 transition-all flex items-center gap-2">
              {t('learnMore')}
            </button>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400">
            <div className="flex items-center gap-2 glass px-4 py-2 rounded-full">
              <span className="w-2 h-2 bg-[#FF007F] rounded-full" />{t('only18')}
            </div>
            <div className="flex items-center gap-2 glass px-4 py-2 rounded-full">
              <Shield className="w-4 h-4 text-[#10B981]" />{t('consentFirst')}
            </div>
            <div className="flex items-center gap-2 glass px-4 py-2 rounded-full">
              <Heart className="w-4 h-4 text-[#E0115F]" />{t('safeModerated')}
            </div>
          </div>
        </div>
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 px-6 relative">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-center mb-4">
            {t('whyChoose')} <span className="gradient-text">FlirtyLive</span>
          </h2>
          <p className="text-slate-400 text-center mb-16 max-w-2xl mx-auto">{t('premiumExp')}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div key={i} className="glass-card rounded-3xl p-6 hover:bg-white/5 transition-all group"
                style={{ animationDelay: `${i * 100}ms` }}>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF007F]/20 to-[#E0115F]/20 flex items-center justify-center text-[#FF007F] mb-4 group-hover:scale-110 transition-transform">
                  {f.icon}
                </div>
                <h3 className="font-semibold text-lg mb-2">{t(f.titleKey)}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{t(f.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Games Preview */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FF007F]/5 to-transparent" />
        <div className="max-w-6xl mx-auto relative">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-center mb-4">{t('partyGames')}</h2>
          <p className="text-slate-400 text-center mb-16 max-w-2xl mx-auto">{t('partyGamesDesc')}</p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { emoji: '\u{1F3AF}', titleKey: 'hotSeat', descKey: 'hotSeatDesc' },
              { emoji: '\u{1F6A9}\u{1F49A}', titleKey: 'redFlagGreenFlag', descKey: 'rfgfDesc' },
              { emoji: '\u{1F3B2}', titleKey: 'truthOrDare', descKey: 'todDesc' },
            ].map((g, i) => (
              <div key={i} className="glass-card rounded-3xl p-8 text-center group hover:border-[#FF007F]/30 transition-all">
                <div className="text-5xl mb-4">{g.emoji}</div>
                <h3 className="font-display text-xl font-semibold mb-2">{t(g.titleKey)}</h3>
                <p className="text-slate-400 text-sm">{t(g.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 relative">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-6">{t('readyToConnect')}</h2>
          <p className="text-slate-400 mb-8">{t('joinThousands')}</p>
          <button data-testid="final-cta-btn" onClick={() => navigate('/auth')}
            className="gradient-button rounded-full px-12 py-4 text-lg font-semibold">
            {t('enterLive')}
          </button>
        </div>
      </section>
    </div>
  );
}
